import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Typography, Space, Button, Form } from 'antd';
import { Table, ApplicationUIContext, Drawer, useDrawerState } from '@yuhuinc/ui-library';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';

import AddLeaseItem from './RentableItems/AddLeaseItem';
import { formatMoney } from 'helpers/Formatter';
import { getTranslatedString } from 'helpers/i18n';

import { useAncillaryItemTypes } from 'components/landlord_app/application_management/common/queries/ancillary_items/useAncillaryItemType';

const { Title } = Typography;

const StyledSpace = styled(Space)`
  width: 100%;

  .ant-picker {
    width: 100%;
  }
`;

const RentableItemsRowActions = ({
  selectedUnit,
  ancillaryItemTypes,
  rentableItem,
  onUpdate,
  onDelete,
  disabled,
}) => {
  const [, editRentableItemDrawer] = useDrawerState(false);

  return (
    <>
      <Space size="middle">
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={editRentableItemDrawer.open}
          disabled={disabled}
        />
        <Button type="link" icon={<DeleteOutlined />} onClick={onDelete} disabled={disabled} />
      </Space>

      <Drawer
        title={getTranslatedString(
          'landlordPortal.applicationManagement.inviteLeadToApply.rentableItems.edit'
        )}
        visible={editRentableItemDrawer.isVisible}
        closable
        destroyOnClose
        onClose={editRentableItemDrawer.close}
      >
        <AddLeaseItem
          ancillaryItemTypes={ancillaryItemTypes}
          initialValues={rentableItem}
          onAdd={onUpdate}
          closeDrawer={editRentableItemDrawer.close}
          propertyId={selectedUnit.property_id}
        />
      </Drawer>
    </>
  );
};

const RentableItemsTable = ({
  selectedRentableItems,
  ancillaryItemTypes,
  operations,
  disabled,
}) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const form = Form.useFormInstance();
  const selectedUnit = form.getFieldValue('selectedUnit');

  const columns = [
    {
      title: getTranslatedString('landlordPortal.rentableItems.table.itemType'),
      dataIndex: 'item_type',
      width: '22.5%',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              return getFieldValue(['lease_items', row.name, 'item_type']);
            }}
          </Form.Item>
        );
      },
    },
    {
      title: getTranslatedString('landlordPortal.rentableItems.table.itemSubType'),
      dataIndex: 'asset_number',
      width: '22.5%',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              return getFieldValue(['lease_items', row.name, 'asset_number']);
            }}
          </Form.Item>
        );
      },
    },
    {
      title: getTranslatedString('landlordPortal.rentableItems.table.marketAmountCents'),
      dataIndex: 'amount',
      width: '22.5%',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              return formatMoney(getFieldValue(['lease_items', row.name, 'amount']));
            }}
          </Form.Item>
        );
      },
    },
    {
      title: getTranslatedString('landlordPortal.rentableItems.table.marketDepositAmountCents'),
      dataIndex: 'deposit_amount',
      width: '22.5%',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              return formatMoney(getFieldValue(['lease_items', row.name, 'deposit_amount']));
            }}
          </Form.Item>
        );
      },
    },
    {
      key: 'actions',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue, setFields }) => {
              return (
                <RentableItemsRowActions
                  selectedUnit={selectedUnit}
                  ancillaryItemTypes={ancillaryItemTypes}
                  rentableItem={getFieldValue(['lease_items', row.name])}
                  onUpdate={(value) =>
                    setFields([
                      {
                        name: ['lease_items', row.name],
                        value,
                      },
                    ])
                  }
                  onDelete={() => operations.remove(row.name)}
                  disabled={disabled}
                />
              );
            }}
          </Form.Item>
        );
      },
    },
  ];

  return (
    <>
      {selectedRentableItems.length > 0 && (
        <Table
          dataSource={selectedRentableItems}
          columns={columns}
          hideActionAndSearch
          size={isMobile ? 'middle' : 'small'}
          scroll={{
            x: isMobile,
          }}
          pagination={false}
        />
      )}
    </>
  );
};

const RentableItems = ({ selectedUnit, disabled }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const [
    rentableItemsDrawerIsVisible,
    { open: openAddRentableItemDrawer, close: closeAddRentableItemDrawer },
  ] = useDrawerState(false);

  const { isLoading: isLoadingAncillaryItemTypes, data: ancillaryItemTypes } =
    useAncillaryItemTypes(selectedUnit.property_id);

  return (
    <>
      {!isLoadingAncillaryItemTypes && (
        <Form.List name="lease_items">
          {(fields, operations) => {
            const addLeaseItem = (leaseItem) => operations.add(leaseItem);

            return (
              <>
                <StyledSpace direction="vertical" size="middle">
                  <Title level={isMobile ? 4 : 5} style={{ margin: 0 }}>
                    {getTranslatedString('landlordPortal.rentableItems')}
                  </Title>

                  <RentableItemsTable
                    selectedRentableItems={fields}
                    ancillaryItemTypes={ancillaryItemTypes}
                    operations={operations}
                    disabled={disabled}
                  />

                  <Button
                    size={isMobile && 'large'}
                    onClick={openAddRentableItemDrawer}
                    disabled={disabled}
                  >
                    {getTranslatedString(
                      'landlordPortal.applicationManagement.inviteLeadToApply.rentableItems.add'
                    )}
                  </Button>
                </StyledSpace>

                <Drawer
                  title={getTranslatedString(
                    'landlordPortal.applicationManagement.inviteLeadToApply.rentableItems.drawerTitle'
                  )}
                  visible={rentableItemsDrawerIsVisible}
                  onClose={closeAddRentableItemDrawer}
                  closable
                  destroyOnClose
                >
                  <AddLeaseItem
                    ancillaryItemTypes={ancillaryItemTypes}
                    onAdd={addLeaseItem}
                    closeDrawer={closeAddRentableItemDrawer}
                    propertyId={selectedUnit.property_id}
                  />
                </Drawer>
              </>
            );
          }}
        </Form.List>
      )}
    </>
  );
};

RentableItems.propTypes = {
  selectedUnit: PropTypes.object,
  disabled: PropTypes.bool,
};

export default RentableItems;
